'use client';
import { Image } from '@bw/elements';
import { Loader } from '@bw/elements/icons';
import { PodcastItem } from '@bw/external-apis/shared';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { useEffect, useState } from 'react';
import { useAppContext } from '../form-stepper/StepperProvider';
import { PodcastPlayer } from '../podcast-player/PodcastPlayer';
import { Button } from '../utility/button/button';
import { FunctionalityButton } from '../utility/functionality-button/functionalityButton';
import styles from './PodcastBannerRichText.module.scss';
import { ImageRichTextButton } from '@bw/apis/contentful';
import { nonNull } from '@bw/core';
import { ThemeColor } from '../../theme';

type PodcastBannerRichTextProps = {
	fields: ImageRichTextButton['fields'];
	podcastItems: PodcastItem[];
};

export function PodcastView({
	fields,
	podcastItems,
}: PodcastBannerRichTextProps) {
	const [podcastData, setPodcastData] = useState<PodcastItem>(podcastItems[0]);
	const [playPodcast, setPlayPodcast] = useState<boolean>(false);
	const { podcastPopupDisplay, setPodcastPopupDisplay } = useAppContext();
	const [loading, setLoading] = useState(true);

	const optionHeading = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
				return <h2 data-aos="fade-up">{children}</h2>;
			},
		},
	};
	const options_method = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
				return <p data-aos="fade-up">{children}</p>;
			},
		},
	};

	const getPodcastData = () => {
		try {
			setPodcastData(podcastItems[0]);
			setLoading(false); // Set loading to false once data is received
		} catch (error) {
			console.error(error);
			setLoading(false); // Set loading to false in case of an error
		}
	};

	useEffect(() => {
		getPodcastData();
	}, []);

	const handlePlayLatestEpisode = () => {
		setPlayPodcast(true);
		setPodcastPopupDisplay(true);
	};

	const bannerImage = fields.image?.fields.image?.fields;

	return (
		<>
			<div className={styles['PodcastBanner']}>
				{loading ? (
					// Display a loader while data is being fetched
					<div className="is__pageloader">
						<figure className={styles.loader} data-aos="fade-up">
							<Loader width={100} height={100} />
						</figure>
					</div>
				) : (
					<div className="container">
						<div className="PodcastBanner__row" data-aos="fade-up">
							<div className="PodcastBanner__img">
								<figure data-aos="fade-up">
									<Image
										alt={bannerImage?.title ?? ''}
										src={bannerImage?.file?.url ?? ''}
										height={bannerImage?.file?.details?.image?.height ?? 0}
										width={bannerImage?.file?.details?.image?.width ?? 0}
									/>
								</figure>
							</div>
							<div
								className="PodcastBanner__content"
								data-aos="fade-up"
								data-aos-delay="200"
							>
								<span className="headingTagline" data-aos="fade-up">
									{fields.headingTagline}
								</span>
								{fields.headingRichText &&
									documentToReactComponents(
										fields.headingRichText,
										optionHeading
									)}

								{fields.description &&
									documentToReactComponents(fields.description, options_method)}

								<div className="PodcastBanner__buttons">
									<FunctionalityButton
										title="Play the latest episode"
										textColor="deepBlue"
										backgroundColor="brightYellow"
										borderColor="brightYellow"
										containsPlayIcon={true}
										onClick={handlePlayLatestEpisode}
									/>

									{nonNull(fields.buttons)
										.map((button) => button.fields)
										.map((button, index) => (
											<Button
												backgroundColor={
													(button.backgroundColour ?? 'none') as ThemeColor
												}
												textColor={(button.textColour ?? 'black') as ThemeColor}
												href={button.buttonUrl ?? ''}
												borderColor={
													(button.borderColour ?? 'black') as ThemeColor
												}
												title={button.title ?? ''}
												key={index}
												playIcon={button.containsPlayIcon}
												openInNewTab={button.openInNewTab ?? false}
											/>
										))}
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
			{playPodcast && <PodcastPlayer podcastData={podcastData} />}
		</>
	);
}
